import { ArrowLeft, CheckCircle, Circle } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';

import {
  AddTicketNumberSequenceMutation as AddTicketNumberSequence,
  PurchaseStatus,
} from '__generated__/graphql';

import { SectionHeading } from '../../../common/UI/Text';
import { assetUrl, encodeUrl, isGraphQLValidationError } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddTicketNumberSequenceForm from './AddTicketNumberSequenceForm';
import AssignTicketNumbersMutation from './AssignTicketNumbersMutation';
import EventPicker, { Event } from '../../Events/EventPicker';
import GetEventsCountQuery from '../GetEventsCountQuery';
import GetTicketNumberSequencesForEventQuery from './GetTicketNumberSequencesForEventQuery';
import NoEventsCard from '../NoEventsCard';
import RevokeTicketNumbersMutation from './RevokeTicketNumbersMutation';
import TicketNumberSequenceCard from './TicketNumberSequenceCard';
import ToggleAutoAssignTicketNumbersMutation from './ToggleAutoAssignTicketNumbersMutation';
import TogglePublishTicketNumbersMutation from './TogglePublishTicketNumbersMutation';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useScroll from '../../../common/useScroll';

 type AddedTicketNumberSequence = AddTicketNumberSequence['addTicketNumberSequence'];

export interface TicketNumbersPageProps {
  ticketNumberSequenceId?: string;
}

const TicketNumbersPage = ({ ticketNumberSequenceId }: TicketNumbersPageProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const { scrollToIfInvisible } = useScroll();
  const history = useHistory();
  const project = useProject();
  const notifier = useNotifier();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('ticket_numbers'));

  const { data: eventsData } = useQuery(
    GetEventsCountQuery,
    {
      variables: {
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data, loading, refetch } = useQuery(
    GetTicketNumberSequencesForEventQuery,
    {
      variables: {
        project_id: project.id,
        event_id: recentEvent.id,
      },
      onError: (error) => {
        if (isGraphQLValidationError(error)) {
          // Recent event no longer exists, reset it.
          recentEvent.reset();
        }
      },
      skip: !recentEvent.id,
    },
  );

  const event = data?.project.event.id === recentEvent.id ? data?.project.event : null;
  const sequences = event?.ticket_number_sequences;

  const [openAddTicketNumberSequenceModal, closeAddTicketNumberSequenceModal] = useModal(() => {
    const handleSuccess = (sequence: AddedTicketNumberSequence) => {
      closeAddTicketNumberSequenceModal();
      history.push(`/${project.id}/participants/bib-numbers/${sequence.id}?${encodeUrl({ event: recentEvent.id })}`);
    };

    return (
      <UI.Modal maxWidth={600} close={closeAddTicketNumberSequenceModal}>
        <AddTicketNumberSequenceForm
          event={event}
          onSuccess={handleSuccess}
        />
      </UI.Modal>
    );
  }, [project, event]);

  useEffect(() => {
    if (sequences?.filter(({ id }) => id === ticketNumberSequenceId).length === 0) {
      refetch().then(() => {
        scrollToIfInvisible(`TicketNumberSequenceCard_${ticketNumberSequenceId}`, { gutter: 0.5 });
      });
    }
  }, [sequences, ticketNumberSequenceId, refetch, scrollToIfInvisible]);

  const [assignTicketNumbers, { loading: assigning }] = useMutation(
    AssignTicketNumbersMutation,
    {
      variables: {
        input: {
          event: { id: event?.id },
        },
      },
      optimisticResponse: {
        assignTicketNumbers: {
          id: event?.id,
          assigning_ticket_numbers: true,
          assigned_ticket_number_count: event?.assigned_ticket_number_count,
          missing_ticket_number_count: event?.missing_ticket_number_count,
          __typename: 'Event',
        },
      },
    },
  );

  const assigningRef = useRef(false);

  useEffect(() => {
    if (event) {
      if (event.assigning_ticket_numbers) {
        assigningRef.current = true;
        let timer: number;

        const startRefetching = (count: number = 1) => {
          timer = window.setTimeout(() => {
            refetch();

            startRefetching(count + 1);
          }, Math.min(count * 2000, 10000));
        };

        startRefetching();

        return () => clearTimeout(timer);
      }

      if (assigningRef.current && !event.assigning_ticket_numbers) {
        notifier.success(t('notifications.ticket_numbers_assigned'));
        assigningRef.current = false;
      }
    }

    return undefined;
  }, [event, notifier, refetch, t]);

  const [revokeTicketNumbers, { loading: revoking }] = useMutation(
    RevokeTicketNumbersMutation,
    {
      variables: {
        input: {
          event: { id: event?.id },
        },
      },
      onCompleted: () => notifier.success(t('notifications.ticket_numbers_reset')),
      onError: () => notifier.error(t('notifications.something_went_wrong')),
    },
  );

  const [confirmAssign] = useConfirmModal({
    title: t('assign_ticket_numbers'),
    body: event && (
      <UI.Div>
        {t('confirm_assign_ticket_numbers')}
        {event?.assigned_ticket_number_count > 0 && (
          <UI.Info sc={{ mt: 2 }}>
            <Trans
              i18nKey="existing_ticket_numbers_are_kept_description"
              values={{
                count: event.assigned_ticket_number_count,
                value: formatInteger(event.assigned_ticket_number_count),
              }}
            >
              <UI.Strong />
            </Trans>
          </UI.Info>
        )}
      </UI.Div>
    ),
    onConfirm: () => assignTicketNumbers(),
  }, [event]);

  const [confirmRevoke] = useConfirmModal({
    title: t('revoke_ticket_numbers'),
    body: event && (
      <UI.Div>
        {t('confirm_revoke_ticket_numbers')}
        {event.locked_ticket_number_count > 0 && (
        <UI.Info sc={{ mt: 2 }}>
          <Trans
            i18nKey="manual_ticket_numbers_are_kept_description"
            values={{
              count: event.locked_ticket_number_count,
              value: formatInteger(event.locked_ticket_number_count),
            }}
          >
            <UI.Strong />
          </Trans>
        </UI.Info>
        )}
      </UI.Div>
    ),
    onConfirm: () => revokeTicketNumbers(),
  }, [event]);

  const [toggleAutoAssignTicketNumbers] = useMutation(
    ToggleAutoAssignTicketNumbersMutation,
    {
      variables: {
        input: {
          id: event?.id,
          enabled: !event?.auto_assign_ticket_numbers,
        },
      },
      optimisticResponse: {
        toggleAutoAssignTicketNumbers: {
          id: event?.id,
          auto_assign_ticket_numbers: !event?.auto_assign_ticket_numbers,
          __typename: 'Event',
        },
      },
      onCompleted: ({ toggleAutoAssignTicketNumbers: event }) => {
        notifier.success(
          t(`notifications.auto_assign_ticket_numbers_${event.auto_assign_ticket_numbers ? 'enabled' : 'disabled'}`),
        );
      },
    },
  );

  const [togglePublishTicketNumbers] = useMutation(
    TogglePublishTicketNumbersMutation,
    {
      variables: {
        input: {
          id: event?.id,
          enabled: !event?.ticket_numbers_published,
        },
      },
      optimisticResponse: {
        togglePublishTicketNumbers: {
          id: event?.id,
          ticket_numbers_published: !event?.ticket_numbers_published,
          __typename: 'Event',
        },
      },
      onCompleted: ({ togglePublishTicketNumbers: event }) => {
        notifier.success(
          t(`notifications.ticket_numbers_${event.ticket_numbers_published ? 'published' : 'unpublished'}`),
        );
      },
    },
  );

  const handleEventChange = (event?: Event) => {
    history.push(`/${project.id}/participants/bib-numbers?${encodeUrl({ event: event?.id })}`);
  };

  if (!eventsData || (recentEvent.id && !event)) {
    return <UI.PageLoader />;
  }

  if (eventsData.project.events.total === 0) {
    return <NoEventsCard />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.Div>
          <UI.SectionHeading sc={{ inlineBlock: true, my: 0, mr: 2 }}>
            {t('ticket_numbers')}
          </UI.SectionHeading>
        </UI.Div>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'], alignVertical: 'center' }} style={{ zIndex: 10 }}>
          <UI.Div>
            <EventPicker
              eventId={recentEvent.id}
              onChange={handleEventChange}
              nullable={false}
              renderActive={(event) => (
                <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                  {event?.title || t('choose_event')}
                </UI.Span>
              )}
            />
          </UI.Div>
          {!recentEvent.id && (
            <UI.Div>
              <UI.Card
                sc={{ py: 0 }}
                style={{ lineHeight: '40px', borderRadius: '8px !important' }}
              >
                <UI.Icon sc={{ muted: true }}>
                  <ArrowLeft />
                </UI.Icon>
                {' '}
                {t('select_event_first')}
              </UI.Card>
            </UI.Div>
          )}
          {event && sequences?.length > 0 && (
            <UI.Div style={{ marginLeft: 'auto' }}>
              {event.assigned_ticket_number_count === 0 && (
                <>
                  <UI.StatusIndicator
                    sc={{ background: 'error' }}
                  />
                  {' '}
                  {t('no_ticket_numbers_assigned')}
                </>
              )}
              {event.missing_ticket_number_count === 0
                && event.assigned_ticket_number_count > 0 && (
                <>
                  <UI.StatusIndicator
                    sc={{ background: 'success' }}
                  />
                  {' '}
                  {t('all_ticket_numbers_assigned')}
                </>
              )}
              {event.assigned_ticket_number_count > 0 && event.missing_ticket_number_count > 0 && (
                <>
                  <UI.StatusIndicator
                    sc={{ background: 'info' }}
                  />
                  {' '}
                  <UI.Delimit>
                    <Trans
                      i18nKey="ticket_numbers_are_missing"
                      values={{
                        count: event.missing_ticket_number_count,
                        value: formatInteger(event.missing_ticket_number_count),
                      }}
                    >
                      <UI.Strong />
                    </Trans>
                    <UI.A
                      href={`/b/${project.id}/participants?${encodeUrl({
                        event: event.id,
                        has_ticket_number: false,
                        statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
                      })}`}
                      target="_blank"
                    >
                      {t('view')}
                    </UI.A>
                  </UI.Delimit>
                </>
              )}
              {(event.assigning_ticket_numbers || assigning || revoking) && (
                <UI.Loader sc={{ brand: 'gray.400', ml: 1 }} />
              )}
            </UI.Div>
          )}
        </UI.GridContainer>

        {event && sequences?.length > 0 && (
          <>
            <UI.Card sc={{ outline: 'gray.200' }}>
              <UI.GridContainer
                sc={{
                  columns: ['1fr', '1fr 1fr 1fr'],
                  rowTemplate: 'auto auto',
                  autoColumns: [false, true],
                  gutter: 0.5,
                  muted: loading,
                }}
              >
                <UI.Div>
                  <UI.H5 sc={{ color: sequences?.length > 0 ? 'secondary.800' : undefined, mb: 1 }}>
                    <UI.Icon sc={{ muted: event.assigned_ticket_number_count === 0 }}>
                      {event.assigned_ticket_number_count > 0 && <CheckCircle />}
                      {event.assigned_ticket_number_count === 0 && <Circle />}
                    </UI.Icon>
                    {' '}
                    {t('ticket_numbers_assign_title')}
                  </UI.H5>
                  <UI.InputDescription style={{ minHeight: 84 }}>
                    {t('ticket_numbers_assign_description')}
                  </UI.InputDescription>
                </UI.Div>
                <UI.Div sc={{ spaceX: 1 }}>
                  <UI.Button
                    onClick={() => confirmAssign()}
                    disabled={event.missing_ticket_number_count === 0 || sequences?.length === 0
                      || event.assigning_ticket_numbers || assigning || revoking}
                    sc={{
                      brand: 'secondary',
                      loading: event.assigning_ticket_numbers || assigning,
                      size: 'sm',
                    }}
                  >
                    {t('assign_all')}
                  </UI.Button>
                  {event.assigned_ticket_number_count - event.locked_ticket_number_count > 0 && (
                    <UI.Button
                      onClick={() => confirmRevoke()}
                      sc={{
                        outline: true,
                        color: 'gray',
                        loading: revoking,
                        size: 'sm',
                      }}
                      disabled={event.assigning_ticket_numbers || assigning || revoking}
                    >
                      {t('revoke_all')}
                    </UI.Button>
                  )}
                </UI.Div>
                <UI.Div>
                  <UI.H5 sc={{ color: 'secondary.800', mb: 1 }}>
                    <UI.Icon sc={{ muted: event.auto_assign_ticket_numbers }}>
                      {event.auto_assign_ticket_numbers && <CheckCircle />}
                      {!event.auto_assign_ticket_numbers && <Circle />}
                    </UI.Icon>
                    {' '}
                    {t('ticket_numbers_automate_title')}
                  </UI.H5>
                  <UI.InputDescription style={{ minHeight: 84 }}>
                    {t('ticket_numbers_automate_description')}
                  </UI.InputDescription>
                </UI.Div>
                <UI.Div sc={{ py: 0.5 }}>
                  <UI.Toggle
                    onChange={() => toggleAutoAssignTicketNumbers()}
                    checked={event.auto_assign_ticket_numbers}
                    aria-label={t(event.auto_assign_ticket_numbers ? 'enabled' : 'disabled')}
                  />
                  <UI.Legend sc={{ ml: 2, inlineBlock: true }}>
                    {t(event.auto_assign_ticket_numbers ? 'enabled' : 'disabled')}
                  </UI.Legend>
                </UI.Div>
                <UI.Div>
                  <UI.H5
                    sc={{
                      color: event.assigned_ticket_number_count > 0 ? 'secondary.800' : undefined,
                      mb: 1,
                    }}
                  >
                    <UI.Icon sc={{ muted: !event.ticket_numbers_published }}>
                      {event.ticket_numbers_published && <CheckCircle />}
                      {!event.ticket_numbers_published && <Circle />}
                    </UI.Icon>
                    {' '}
                    {t('ticket_numbers_publish_title')}
                  </UI.H5>
                  <UI.InputDescription style={{ minHeight: 84 }}>
                    {t('ticket_numbers_publish_description')}
                  </UI.InputDescription>
                </UI.Div>
                <UI.Div sc={{ py: 0.5 }}>
                  <UI.Toggle
                    onChange={() => togglePublishTicketNumbers()}
                    checked={event.ticket_numbers_published}
                    aria-label={t(event.ticket_numbers_published ? 'unpublish' : 'publish')}
                    disabled={event.assigned_ticket_number_count === 0}
                  />
                  <UI.Legend sc={{ ml: 2, inlineBlock: true }}>
                    {t(event.ticket_numbers_published ? 'published' : 'invisible')}
                  </UI.Legend>
                </UI.Div>
              </UI.GridContainer>
            </UI.Card>

            <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
              <SectionHeading>
                {t('ticket_number_sequences')}
              </SectionHeading>
              <UI.Button
                onClick={openAddTicketNumberSequenceModal}
                sc={{ brand: 'secondary' }}
                disabled={event.assigning_ticket_numbers}
              >
                {t('add_ticket_number_sequence')}
              </UI.Button>
            </UI.FlexContainer>

            <UI.GridContainer sc={{ muted: loading, gutter: 1 / 3 }} style={{ zIndex: 1 }}>
              {sequences.map((sequence) => (
                <TicketNumberSequenceCard
                  sequence={sequence}
                  event={event}
                  active={ticketNumberSequenceId === sequence.id}
                  muted={ticketNumberSequenceId && ticketNumberSequenceId !== sequence.id}
                  key={sequence.id}
                />
              ))}
            </UI.GridContainer>
          </>
        )}

        {(!recentEvent.id || sequences?.length === 0) && (
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
            <UI.Div style={{ maxWidth: 600, margin: '0 auto' }}>
              <img src={assetUrl('images/ticket-numbers.svg')} alt="" height={120} />
              <UI.H3 sc={{ mb: 2 }}>{t('ticket_numbers')}</UI.H3>
              <UI.Paragraph sc={{ mb: 0 }}>
                {t('ticket_numbers_get_started')}
              </UI.Paragraph>
              {event && (
                <UI.Button
                  onClick={openAddTicketNumberSequenceModal}
                  sc={{ mt: 3, brand: 'secondary' }}
                  disabled={event.assigning_ticket_numbers}
                >
                  {t('add_ticket_number_sequence')}
                </UI.Button>
              )}
            </UI.Div>
          </UI.Card>
        )}
      </UI.GridContainer>
    </UI.Section>
  );
};

export default TicketNumbersPage;
